/* eslint-disable no-console */
import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import axios from 'axios';
import { cart } from '@/theme/Cart';
import { parseUrl, stringifyUrl } from 'query-string';

interface ZapietEvents {
    ['selected_method'](payload: 'delivery'|'pickup'|'shipping'): void;
}

@Component
export class CartMain extends ThemeComponent {
    @Prop({ default: 'cart-main' }) sectionId!: string;
    @Prop pickupOnly!: string;

    @Ref() updatingCart!: HTMLElement;
    @Ref({ required: false}) checkoutButton!: HTMLElement;

    zapietWidget!: HTMLElement; // No @Ref, will manually set this value

    // Store error messages temporarily in memory
    private errorMessage: string = '';

    init() {
        // Listen for cart item added
        document.addEventListener('cartChanged', (e: Event) => {
            // Handle item changes
            void this.updateItemsMarkup();

            // Add body class
            document.body.classList.add('cart-has-changed-since-load');

            if (this.checkoutButton) {
                if (cart.isEmpty()) {
                    this.checkoutButton.classList.add('button--disabled');
                } else {
                    this.checkoutButton.classList.remove('button--disabled');
                }
            }

        });

        function waitForZapietObject() {
            return new Promise((resolve, reject) => {
                const intervalId = setInterval(() => {
                    if (window.ZapietEvent) {
                        clearInterval(intervalId);
                        resolve(window.ZapietEvent);
                    }
                }, 100); // Check every 100 milliseconds for availability of thatObject
            });
        }


        // Function to wait for ZapietObject to become available
        if (typeof window.ZapietEvent === 'undefined') {
            void waitForZapietObject().then((thatObject) => {
                this.zapietEventListeners();
            });
        } else {
            this.zapietEventListeners();
        }


    }

    zapietEventListeners() {

        window.ZapietEvent?.listen('selected_method', (payload: 'delivery' | 'pickup' | 'shipping') => {
            if (this.checkoutButton) {
                this.checkoutButton.classList.add('button--disabled');
                this.checkoutButton.innerText = 'Please Wait';
            }

            if (payload === 'delivery' || this.pickupOnly == 'true') {
                this.setLoading(false);
            } else {
                this.setLoading(true);
            }
        });

        window.ZapietEvent?.listen('checkoutEnabled', () => {
            if (this.checkoutButton) {
                this.checkoutButton.classList.remove('button--disabled');
                this.checkoutButton.innerText = 'Checkout Now';
                if (cart.isEmpty()) {
                    this.checkoutButton.classList.add('button--disabled');
                }
            }
            this.setLoading(false);
        });

        window.addEventListener('load', () => {
            const hasGiftCard = cart.items.some((item) => item.handle === 'gift-card');
            if (hasGiftCard && this.checkoutButton) {
                this.checkoutButton.classList.remove('button--disabled');
            }
        });

        const observer = new MutationObserver(() => {
            const checkoutButton = this.querySelector('.checkout-button');
            if (checkoutButton) {
                const hasGiftCard = cart.items.some((item) => item.handle === 'gift-card');
                if (hasGiftCard) {
                    checkoutButton.classList.remove('button--disabled');
                }
                observer.disconnect(); // Stop observing after finding the button
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

    }


    public setLoading(loading: boolean = false) {
        if (loading) {
            this.updatingCart.classList.add('loading-spinner--active');
        } else {
            this.updatingCart.classList.remove('loading-spinner--active');
        }
    }

    public async updateItemsMarkup() {

        // Store any existing error message before markup update
        const currentErrorMessageElement = this.querySelector('.cart-error');
        if (currentErrorMessageElement) {
            this.errorMessage = currentErrorMessageElement.textContent || '';
        }

        // Build URL and get latest cart main markup
        const { url, query } = parseUrl(window.location.href, { decode: false });
        const newUrl = stringifyUrl({
            url: url,
            query: {
                ...query,
                sections: `${this.sectionId}`,
            }
        }, { arrayFormat: 'none', encode: false });
        const res = await axios.get(newUrl);

        // Prepare correct markup
        const newCartMain = document.createElement('div');
        newCartMain.innerHTML = res.data[`${this.sectionId}`];
        const newCartMainContainer = newCartMain.querySelector('.cart-main__container');
        const currentCartMainContainer = this.querySelector('.cart-main__container');
        if (newCartMainContainer && currentCartMainContainer) {
            currentCartMainContainer.outerHTML = newCartMainContainer.outerHTML;
        }

        // Restore the error message after updating the cart markup
        const updatedErrorMessageElement = document.querySelector('.cart-error');
        if (updatedErrorMessageElement && this.errorMessage) {
            updatedErrorMessageElement.textContent = this.errorMessage;
            updatedErrorMessageElement.classList.add('show');
        }

        // Ensure updated references and event listeners are added
        this.updateZapietReferences();

        this.setLoading(false);

        // Set cart main loading
        if (this.closest('.side-cart')) {
            this.setLoading(false);
        }

    }

    public updateZapietReferences() {
        // Update checkout button and zapiet widget references after the markup change
        this.checkoutButton = this.querySelector('.cart__checkout-button') as HTMLElement;
        this.zapietWidget = this.querySelector('#storePickupApp') as HTMLElement;

        // Attach event listeners for Zapiet if not already attached
        if (window.ZapietEvent) {
            this.zapietEventListeners();
        }
    }

}
